import {useEffect, useState} from 'react';
import * as XLSX from 'xlsx';
import {fetchAdminFeedback} from '@/services/api';
import {formatDateTime, normalizeMonthSelection} from '@/utils/format.ts';
import {useAppStore} from '@/store/appStore.ts';
import type {AdminFeedbackResponse} from '@/types';
import {YearMonthSelecter} from "@/components/common/YearMonthSelecter.tsx";

const ADMIN_TABLE_HEADERS = ['Platform', 'Hospital', 'Score Month', 'Result', 'IRAI', 'Submit Time', 'Submitter', 'Review', 'Tags'];

function StatCard({title, value, icon}: { title: string; value: number; icon: string }) {
    return (
        <div style={{
            flex: 1, background: '#fff', border: '1px solid #ececec', borderRadius: 2,
            padding: '20px 22px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
            <div>
                <div style={{fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 2}}>{title}</div>
                <div style={{fontSize: 36, lineHeight: 1, fontWeight: 700, color: '#111827'}}>{String(value)}</div>
            </div>
            <div style={{
                width: 64, height: 64, borderRadius: 12, background: '#f3f4f6',
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626', fontSize: 26,
            }}>
                {icon}
            </div>
        </div>
    );
}

function resultColor(r: string) {
    if (r === 'High') return '#dc2626';
    if (r === 'Medium') return '#eab308';
    if (r === 'Low') return '#22c55e';
    return '#64748b';
}

function tagStyle(tag: string) {
    const positive = ['预警准确', '预警及时', '预警有帮助'];
    return positive.includes(tag)
        ? {background: '#e8f7ec', color: '#4b5563'}
        : {background: '#fbeaec', color: '#6b7280'};
}

export default function AdminBackendApp() {
    const hasPermission = useAppStore((s) => s.hasPermission);
    const [month, setMonth] = useState<string[]>([]);
    const [platform, setPlatform] = useState('');
    const [search, setSearch] = useState('');
    const [data, setData] = useState<AdminFeedbackResponse>({
        availableBatches: [],
        rows: [],
        stats: {feedbackCount: 0, abnormalHospitalCount: 0}
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const authorized = hasPermission('adminBackend');

    // Load data
    useEffect(() => {
        if (!authorized) return;
        setLoading(true);
        setError('');
        const params: Record<string, string> = {};
        params.month = normalizeMonthSelection(month).join(',');
        if (platform) params.platform = platform;
        if (search.trim()) params.search = search.trim();

        fetchAdminFeedback(params)
            .then((result) => {
                setData(result);
                if (normalizeMonthSelection(month).length === 0 && result.availableBatches?.length) {
                    setMonth([result.availableBatches[result.availableBatches.length - 1]]);
                }
            }).finally(() => setLoading(false));
    }, [authorized, month, platform, search]);

    const exportExcel = () => {
        if (!data) return;
        const selectedMonths = normalizeMonthSelection(month);
        const exportData = data.rows
            .filter((r) => (selectedMonths.length === 0 || selectedMonths.includes(r.month)) && (r.platform === platform || platform === ''))
            .map((r) => ({
                '反馈ID': r.feedbackId,
                '平台': r.platform,
                '医院编号': r.hospitalCode,
                '医院名称': r.hospitalName,
                '评级月份': r.month,
                'IRAI分数': r.iraiScore,
                '评级结果': r.result,
                '提交时间': formatDateTime(r.submitDate),
                '提交人': r.owner,
                '审核': r.review,
                '标签': r.tags.join(', '),
            }));
        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        const buf = XLSX.write(wb, {bookType: 'xlsx', type: 'array'});
        const blob = new Blob([buf], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedMonths.length ? selectedMonths.join('_') : 'all'}-${platform || 'allplatform'}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (!authorized) {
        return (
            <div style={{
                height: '100%',
                background: '#f3f4f6',
                overflow: 'auto',
                padding: '24px 20px',
                fontFamily: 'inherit'
            }}>
                <div style={{
                    background: '#fff',
                    border: '1px solid #ececec',
                    borderRadius: 2,
                    padding: '20px 22px',
                    color: '#64748b',
                    fontSize: 14
                }}>
                    Access denied. Your account does not have Admin Backend permission.
                </div>
            </div>
        );
    }

    const rows = data.rows || [];

    return (
        <div style={{
            height: '100%',
            background: '#f3f4f6',
            overflow: 'auto',
            padding: '18px 20px 22px',
            fontFamily: 'inherit'
        }}>
            <div style={{color: '#ef2e24', fontSize: 34, lineHeight: 1.1, marginBottom: 20}}>IRAI - Admin Backend</div>

            {/* Stats */}
            <div style={{display: 'flex', gap: 14, marginBottom: 16}}>
                <StatCard title={'本月反馈数'} value={data.stats.feedbackCount} icon={'📝'}/>
                <StatCard title={'本月异常医院数'} value={data.stats.abnormalHospitalCount} icon={'🏥'}/>
            </div>

            {/* Filters */}
            <div style={{
                background: '#fff', border: '1px solid #ececec', borderRadius: 2, padding: '10px 12px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14,
            }}>
                <div style={{display: 'flex', gap: 10}}>
                    <YearMonthSelecter
                        availableBatches={useAppStore().availableBatches}
                        defaultValue={useAppStore().selectBatch}
                        selectStyle={{
                            border: '1px solid #e5e7eb', background: '#f9fafb', borderRadius: 10,
                            fontSize: 13, color: '#6b7280', padding: '9px 14px', minWidth: 116, fontFamily: 'inherit',
                        }}
                        onChange={(y,m)=>setMonth([`${y}${m}`])}
                    />

                    <select value={platform} onChange={(e) => setPlatform(e.target.value)}
                            style={{
                                border: '1px solid #e5e7eb',
                                background: '#f9fafb',
                                borderRadius: 10,
                                fontSize: 13,
                                color: '#374151',
                                fontWeight: 600,
                                padding: '9px 14px',
                                minWidth: 144,
                                fontFamily: 'inherit'
                            }}>
                        <option value="">{'所有平台'}</option>
                        <option value="BIO">BIO</option>
                    </select>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={'搜索医院、医院代码或提交人...'}
                        style={{
                            width: 350,
                            border: '1px solid #e5e7eb',
                            borderRadius: 10,
                            padding: '10px 14px',
                            background: '#f9fafb',
                            fontSize: 13,
                            color: '#6b7280',
                            outline: 'none',
                            fontFamily: 'inherit'
                        }}
                    />
                    <button onClick={exportExcel}
                            style={{
                                padding: '5px 12px',
                                borderRadius: 5,
                                border: '1px solid #e2e8f0',
                                background: '#f8fafc',
                                color: '#1e293b',
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: 'pointer',
                                fontFamily: 'inherit'
                            }}>
                        {'下载xlsx'}
                    </button>
                </div>
            </div>

            {/* Table */}
            <div style={{background: '#fff', border: '1px solid #ececec', borderRadius: 2, overflow: 'hidden'}}>
                <div style={{
                    display: 'grid', gridTemplateColumns: '0.7fr 2.1fr 1.1fr 1fr 0.8fr 1.3fr 1fr 0.8fr 3fr',
                    padding: '12px 10px', background: '#fafafa', borderBottom: '1px solid #ececec',
                    color: '#6b7280', fontSize: 12, fontWeight: 600,
                }}>
                    {ADMIN_TABLE_HEADERS.map((h) => <div key={h}>{h}</div>)}
                </div>
                {loading ? (
                    <div style={{padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 12}}>Loading admin
                        data...</div>
                ) : error ? (
                    <div style={{padding: 24, textAlign: 'center', color: '#dc2626', fontSize: 12}}>{error}</div>
                ) : rows.length === 0 ? (
                    <div style={{padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 12}}>No feedback
                        found</div>
                ) : rows.map((row, idx) => (
                    <div key={row.feedbackId || (row.hospitalCode + '-' + row.month)} style={{
                        display: 'grid', gridTemplateColumns: '0.7fr 2.1fr 1.1fr 1fr 0.8fr 1.3fr 1fr 0.8fr 3fr',
                        padding: '14px 10px', borderBottom: idx === rows.length - 1 ? 'none' : '1px solid #f1f5f9',
                        color: '#6b7280', fontSize: 12, alignItems: 'center',
                    }}>
                        <div>{row.platform}</div>
                        <div style={{color: '#4b5563'}}>{row.hospitalName} ({row.hospitalCode})</div>
                        <div>{row.month}</div>
                        <div style={{color: resultColor(row.result), fontWeight: 700}}>{row.result}</div>
                        <div>{row.iraiScore == null ? '--' : Number(row.iraiScore).toFixed(4)}</div>
                        <div>{formatDateTime(row.submitDate)}</div>
                        <div>{row.owner || '--'}</div>
                        <div style={{
                            color: row.review === 'True' ? '#22c55e' : '#dc2626',
                            fontWeight: 700
                        }}>{row.review || '--'}</div>
                        <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                            {(row.tags || []).map((tag) => (
                                <span key={tag} style={{
                                    fontSize: 11,
                                    padding: '6px 12px',
                                    borderRadius: 6,
                                    whiteSpace: 'nowrap', ...tagStyle(tag)
                                }}>
                  {tag}
                </span>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
